import { Injectable, computed, inject, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { tap } from 'rxjs';

import type { OrganisationSettings } from '../models/organisation-settings.model';
import {
  ORGANISATION_SETTING_KEYS,
  type OrganisationSettingsDefaults,
} from '../models/organisation-settings.model';
import { ApiService } from './api.service';

const DEFAULTS: OrganisationSettingsDefaults = {
  baseCurrency: 'USD',
  currencySymbol: '$',
  timezone: 'UTC',
  dateFormat: 'Y-m-d',
};

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly api = inject(ApiService);

  private readonly organisationState = signal<OrganisationSettings | null>(null);
  private readonly loadedState = signal(false);

  readonly organisation = this.organisationState.asReadonly();
  readonly isLoaded = this.loadedState.asReadonly();

  readonly baseCurrency = computed(
    () =>
      (this.organisationState()?.[ORGANISATION_SETTING_KEYS.baseCurrency] as string | undefined) ??
      DEFAULTS.baseCurrency,
  );

  readonly currencySymbol = computed(
    () =>
      (this.organisationState()?.[ORGANISATION_SETTING_KEYS.currencySymbol] as
        | string
        | undefined) ?? DEFAULTS.currencySymbol,
  );

  readonly timezone = computed(
    () =>
      (this.organisationState()?.[ORGANISATION_SETTING_KEYS.timezone] as string | undefined) ??
      DEFAULTS.timezone,
  );

  readonly logoPath = computed(
    () => (this.organisationState()?.[ORGANISATION_SETTING_KEYS.logoPath] as string | undefined) ?? '',
  );

  readonly faviconPath = computed(
    () => (this.organisationState()?.[ORGANISATION_SETTING_KEYS.faviconPath] as string | undefined) ?? '',
  );

  load(): Observable<OrganisationSettings> {
    return this.api.get<OrganisationSettings>('settings/organisation').pipe(
      tap((settings) => {
        this.organisationState.set(settings);
        this.loadedState.set(true);
      }),
    );
  }

  /**
   * Merge a partial settings record into the current state.
   * Call this after a successful settings save so signals react immediately
   * (e.g. ExchangeRateService re-fetches rates when baseCurrency changes).
   */
  patch(partial: OrganisationSettings): void {
    this.organisationState.update((current) => ({
      ...(current ?? {}),
      ...partial,
    }));
  }

  /** Applies defaults when the API is unavailable (offline / permission). */
  applyDefaults(): void {
    this.organisationState.set({
      [ORGANISATION_SETTING_KEYS.baseCurrency]: DEFAULTS.baseCurrency,
      [ORGANISATION_SETTING_KEYS.currencySymbol]: DEFAULTS.currencySymbol,
      [ORGANISATION_SETTING_KEYS.timezone]: DEFAULTS.timezone,
      [ORGANISATION_SETTING_KEYS.dateFormat]: DEFAULTS.dateFormat,
    });
    this.loadedState.set(true);
  }
}
